// Daily mood picker — BottomSheet (W36.0, spec §2.3).
//
// Spec §2.3 R4.1 is the whole design of this component: reuse the diary
// module's locked 30-code taxonomy and expose **only the six level-3
// categories**. Every value here is derived from `lib/diary/mood.ts` — the
// emoji come from `EMOJI_FOR_CATEGORY`, the labels from
// `MOOD_CATEGORY_LABEL_ZH`, and the codes from `moodCode(cat, 3)`.
//
// Nothing here invents an emoji or a label. That is not a style preference:
// AGENTS.md §3 #19's emoji ban is relaxed for diary mood because the 30 emoji
// are *data*, and the same reasoning means the data must come from the one
// place it is defined. Adding a 7th category here would need a new ADR
// (ADR-0014), which is exactly the guard this reuse provides.
//
// The rendered mood code (`mood_happy_3`) is a legal diary code, so the review
// archive can cross-tabulate check-ins against diary moods with no mapping
// layer.

import {
  EMOJI_FOR_CATEGORY,
  MOOD_CATEGORIES,
  MOOD_CATEGORY_LABEL_ZH,
  moodCode,
} from "@/lib/diary/mood";
import { BottomSheet } from "@/components/patterns/BottomSheet";
import { Button } from "@/components/ui/button";
import { useSheetVariant } from "@/lib/hooks/useSheetVariant";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import { CHECKIN_LEVEL } from "./moodCodes";
import { cn } from "@/lib/utils/cn";

export interface MoodPickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Currently stored code for today, or null. */
  currentMood: string | null;
  onSelect: (moodCode: string) => Promise<void> | void;
  isSaving?: boolean;
}

export function MoodPickerSheet({
  open,
  onOpenChange,
  currentMood,
  onSelect,
  isSaving = false,
}: MoodPickerSheetProps) {
  const { variant } = useSheetVariant();

  const handleSelect = async (category: (typeof MOOD_CATEGORIES)[number]) => {
    await onSelect(moodCode(category, CHECKIN_LEVEL));
    onOpenChange(false);
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      title={DASHBOARD_I18N.mood.pickerTitle}
      description={DASHBOARD_I18N.mood.pickerHint}
      data-testid="mood-picker-sheet"
    >
      <div className="grid grid-cols-3 gap-s-2" data-testid="mood-grid">
        {MOOD_CATEGORIES.map((category) => {
          const code = moodCode(category, CHECKIN_LEVEL);
          const selected = currentMood === code;
          return (
            <button
              key={category}
              type="button"
              onClick={() => void handleSelect(category)}
              disabled={isSaving}
              aria-pressed={selected}
              data-testid={`mood-option-${category}`}
              data-mood-code={code}
              className={cn(
                // `min-h-s-12` = 80px (AGENTS.md §3 #17 — a token, not an
                // arbitrary value), comfortably over the DESIGN §15 44px floor
                // while still fitting three rows of 6 inside the sheet. `w-full`
                // is explicit because a grid item's width comes from its
                // column, and the tap-target assertion reads the class.
                "press flex min-h-s-12 w-full flex-col items-center justify-center gap-s-1",
                "rounded-md border p-s-2 transition-colors",
                selected
                  ? "border-hairline bg-ink text-paper"
                  : "border-hairline bg-paper-elevated text-ink-2 hover:bg-paper-deep",
              )}
            >
              {/* Data, not chrome — see module docstring. */}
              <span aria-hidden className="emoji-font text-title-2 leading-none">
                {EMOJI_FOR_CATEGORY[category]}
              </span>
              <span className="text-meta font-semibold">{MOOD_CATEGORY_LABEL_ZH[category]}</span>
            </button>
          );
        })}
      </div>
      {currentMood ? (
        <Button
          variant="ghost"
          className="mt-s-4 w-full"
          onClick={() => onOpenChange(false)}
          data-testid="mood-cancel"
        >
          取消
        </Button>
      ) : null}
    </BottomSheet>
  );
}
